import React, { useState, useCallback, Fragment, useEffect } from 'react';
import { Button, Card, CardBody, Input, Label, FormGroup, Row, Col, Modal, ModalHeader, ModalBody, TabPane, TabContent, ButtonGroup, ListGroup, ListGroupItem } from 'reactstrap';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import Strings from '../../../translations/Translations';
import DatePicker from 'react-datepicker';
import PSO from '../../../services/PSO';
import { toast } from 'react-toastify';
import { useAuth } from '../../../Context/Auth/AuthContext';
import { useHistory } from 'react-router-dom';
import Autocomplete from 'react-google-autocomplete';
import classnames from 'classnames';
import Assets from '../../../services/Assets';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const COVER_IMG_LOCATION = process.env.REACT_APP_COVER_IMG_LOCATION;
const PUBLIC_VIDEOS_URL = process.env.REACT_APP_PUBLIC_VIDEOS;

const schema = Yup.object().shape({
  campaign_name: Yup.string().required(Strings('campaign_name_is_required'))
});

export default function EditCampaignModule() {
  const history = useHistory();
  const { authenticated, toggleLoading, currentCampaign, toggleActiveCampaign, toggleCurrentCampaign } = useAuth();

  const [googleAddress, setGoogleAddress] = useState();

  const [activeTab, setActiveTab] = useState('1');
  const [userVideos, setUserVideos] = useState();

  const [categoriesSet, setCategoriesSet] = useState();
  const [categories, setCategories] = useState();
  const [styles, setStyles] = useState();

  const { register, errors, handleSubmit } = useForm({
    validationSchema: schema,
  });
  const {
    register: registerAdd,
    handleSubmit: handleSubmitAdd,
    reset: resetAdd
  } = useForm();

  const [modalAddRemix, setModalAddRemix] = useState(false);
  const toggleModalAddRemix = useCallback(() => {
    setModalAddRemix(!modalAddRemix);
  }, [setModalAddRemix, modalAddRemix]);

  const [modalConfirmDelete, setModalConfirmDelete] = useState(false);
  const toggleModalConfirmDelete = useCallback(() => {
    setModalConfirmDelete(!modalConfirmDelete);
  }, [setModalConfirmDelete, modalConfirmDelete]);

  const [modalCreateVideo, setModalCreateVideo] = useState(false);
  const toggleModalCreateVideo = useCallback(() => {
    setModalCreateVideo(!modalCreateVideo);
  }, [setModalCreateVideo, modalCreateVideo]);

  const [dateStart, setDateStart] = useState();
  const [dateEnd, setDateEnd] = useState();

  const convertDateToRequest = useCallback((date) => {
    if (date) {
      const convert = new Date(date);
      const isoString = convert.toISOString();

      const year = isoString.substring(0, 4);
      const month = isoString.substring(5, 7);
      const day = isoString.substring(8, 10);
      const final = `${year}-${month}-${day}--00:00`;
      return final;
    }
    return false;
  }, []);

  const convertDateFromRequest = useCallback((date) => {
    if (date) {
      const year = date.substring(0, 4);
      const month = date.substring(5, 7);
      const day = date.substring(8, 10);
      const final = new Date(`${year}-${month}-${day}`);
      return final;
    }
    return false;
  }, []);

  const getCollectionPars = useCallback(async (colid) => {
    let result = false;
    toggleLoading(true);

    try {
      const response = await PSO.getCollectionPars({
        un: authenticated.un,
        al: authenticated.al,
        colid
      });

      if (response.result === 'success' && response.its && response.its.length > 0) {
        result = response.its[0];
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }
    } catch (err) {
      toast.error(Strings('general_error'));
    }

    toggleLoading(false);
    return result;
  }, [authenticated, toggleLoading]);

  const onSubmit = useCallback(async (data) => {
    const editPars = {
      un: authenticated.un,
      al: authenticated.al,
      colid: currentCampaign.colid,
      coln: data.campaign_name,
      cold: data.campaign_description,
    };

    if (data.campaign_local) {
      editPars.colloc = data.campaign_local;
    }
    if (dateStart) {
      editPars.colds = convertDateToRequest(dateStart);
    }
    if (dateEnd) {
      editPars.colde = convertDateToRequest(dateEnd);
    }
    if (data.campaign_pricing) {
      editPars.colpri = data.campaign_pricing;
    }
    if (data.campaign_tickets) {
      editPars.coltic = data.campaign_tickets;
    }
    if (data.campaign_participants) {
      editPars.colpar = data.campaign_participants;
    }
    if (data.campaign_info_url) {
      editPars.colurl = data.campaign_info_url;
    }
    if (data.campaign_custom_url) {
      editPars.colcurl = data.campaign_custom_url;
    }
    if (data.campaign_organization) {
      editPars.colorg = data.campaign_organization;
    }
    if (googleAddress) {
      if (googleAddress.formatted_address) {
        editPars.colad = googleAddress.formatted_address;
      }
      if (googleAddress.lat) {
        editPars.collati = googleAddress.lat;
      }
      if (googleAddress.lng) {
        editPars.collong = googleAddress.lng;
      }
    }
    if (data.campaign_event_type) {
      editPars.colevt = data.campaign_event_type;
    }
    if (data.campaign_event_style) {
      editPars.colevcat = data.campaign_event_style;
    }

    toggleLoading(true);

    try {
      const response = await PSO.modifyCollection(editPars);

      if (response && response.result === 'success') {
        toggleLoading(false);

        toast.success(Strings('campaign_edited'));

        const collectionPars = await getCollectionPars(currentCampaign.colid);
        if (collectionPars) {
          toggleCurrentCampaign(collectionPars);
        }
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
        toggleLoading(false);
      }
    } catch (err) {
      console.log(err)
      toast.error(Strings('general_error'));
      toggleLoading(false);
    }
  }, [authenticated, dateStart, dateEnd, currentCampaign, toggleLoading, getCollectionPars, toggleCurrentCampaign, convertDateToRequest, googleAddress]);

  const setCategory = useCallback((index) => {
    if (index) {
      const target = categoriesSet[index];

      if (target && target.cats && target.cats.length > 0) {
        const cats = target.cats;
        const stylesToSet = [];

        if (categories && categories.its) {
          cats.forEach((cat) => {
            const targetCategory = categories.its.filter((value) => {
              return parseInt(value.id) === parseInt(cat);
            });
            if (targetCategory[0]) {
              stylesToSet.push(targetCategory[0]);
            }
          });
        }

        setStyles(stylesToSet);
      }
    }
  }, [categoriesSet, categories]);

  const getCategories = useCallback(async (current = null) => {
    toggleLoading(true);
    let categories, generalCategories;
    const newFiltered = [];

    try {
      categories = await Assets.getCategoriesSet();
    } catch (err) {
      console.error(`${Strings('error_while_getting_categories')} [ID==01]`);
    }

    try {
      generalCategories = await Assets.getCategories();
    } catch (err) {
      toast.error(`${Strings('error_while_getting_categories')} [ID==02]`);
    }

    if (categories && categories.its) {
      const filtered = categories.its.filter((item, index) => {
        return index !== 0;
      });

      // Changing 'Events' to 'Other Events'
      filtered.forEach((item) => {
        if (item && item.title === 'Events') {
          item.title = 'Other events';
        }
        newFiltered.push(item);
      });
      setCategoriesSet(newFiltered);
    }
    if (generalCategories) {
      setCategories(generalCategories);
    }

    if (current !== undefined && current !== null) {
      const indexToSet = current;
      if (newFiltered) {
        const target = newFiltered[indexToSet];

        if (target && target.cats && target.cats.length > 0) {
          const cats = target.cats;
          const stylesToSet = [];
          if (generalCategories && generalCategories.its) {
            cats.forEach((cat) => {
              const targetCategory = generalCategories.its.filter((value) => {
                return parseInt(value.id) === parseInt(cat);
              });
              if (targetCategory[0]) {
                stylesToSet.push(targetCategory[0]);
              }
            });
          }
          setStyles(stylesToSet);
        }
      }
    }

    toggleLoading(false);
  }, [toggleLoading, setCategoriesSet, setCategories, setStyles]);

  useEffect(() => {
    if (currentCampaign) {
      if (currentCampaign.colds && currentCampaign.colds !== '00') {
        const converted = convertDateFromRequest(currentCampaign.colds);
        if (converted) {
          setDateStart(converted);
        }
      }
      if (currentCampaign.colde && currentCampaign.colde !== '00') {
        const converted = convertDateFromRequest(currentCampaign.colde);
        if (converted) {
          setDateEnd(converted);
        }
      }

      if (currentCampaign.colevcat && currentCampaign.colevcat) {
        getCategories(currentCampaign.colevt);
      } else {
        getCategories();
      }
    }

  }, [currentCampaign, setDateStart, setDateEnd, convertDateFromRequest, getCategories]);

  const deleteCampaign = useCallback(async () => {
    toggleModalConfirmDelete();
  }, [toggleModalConfirmDelete]);

  const removeCollection = useCallback(async () => {
    let result = false;
    toggleLoading(true);

    try {
      const response = await PSO.removeCollection({
        un: authenticated.un,
        al: authenticated.al,
        colid: currentCampaign.colid
      });

      if (response.result === 'success') {
        result = true;
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }
    } catch (err) {
      toast.error(Strings('general_error'));
    }

    toggleLoading(false);
    return result;
  }, [authenticated, currentCampaign, toggleLoading]);

  const confirmDeleteCampaign = useCallback(async () => {
    const collectionDeleted = await removeCollection();
    if (collectionDeleted) {
      toast.success(Strings('campaign_deleted'));
      toggleActiveCampaign(false);
      history.push('/campaigns');
    }
  }, [removeCollection, history, toggleActiveCampaign]);

  const onSubmitAdd = useCallback(async (value) => {
    if (value && value.rid) {
      toggleLoading(true);

      try {
        const response = await PSO.addRemixToCollection({
          un: authenticated.un,
          al: authenticated.al,
          colid: currentCampaign.colid,
          rid: value.rid
        });

        if (response.result === 'success') {
          toast.success(Strings('remix_addded'));
          toggleModalAddRemix();
          resetAdd();
        } else {
          const message = (response && response.error) ? response.error : Strings('error_on_request');
          toast.error(message);
        }
      } catch (err) {
        toast.error(Strings('general_error'));
      }

      toggleLoading(false);
    }
  }, [toggleLoading, authenticated, currentCampaign, toggleModalAddRemix, resetAdd]);

  const convertCreateDate = useCallback((date) => {
    if (typeof date !== 'string') date = `${date}`;
    const y = date.substring(0, 4);
    const m = date.substring(4, 6);
    const d = date.substring(6, 8);
    const h = date.substring(8, 10);
    const min = date.substring(10, 12);
    return `${m}-${d}-${y} ${h}:${min}`;
  }, []);

  const toggleGoogleAddress = useCallback((place) => {
    if (place && place.formatted_address) {
      const address = {
        formatted_address: place.place,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };
      setGoogleAddress(address);
    }
  }, [setGoogleAddress]);

  const getUserVideos = useCallback(async () => {
    setUserVideos();
    toggleLoading(true);

    try {
      const response = await PSO.getRemixListByOwner({
        un: authenticated.un,
        al: authenticated.al,
        username: authenticated.un
      });

      if (response.result === 'success' && response.its && response.its.length > 0) {
        setUserVideos(response.its);
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }
    } catch (err) {
      toast.error(Strings('general_error'));
    }

    toggleLoading(false);

  }, [toggleLoading, authenticated, setUserVideos]);

  return (
    <Fragment>
      <Card>
        <CardBody>
          <Row>
            <Col md={12}>
              <p className='mb-0'>{Strings('review_and_edit_your_campaign_details')}</p>
            </Col>
            <Col>
              <hr />
            </Col>
          </Row>

          {currentCampaign && currentCampaign.coln && (
            <Row className='mb-2'>
              <Col md={12}>
                <h2 className='mb-0'>{currentCampaign.coln}</h2>
                <p><strong>{Strings('created')}:</strong> {convertCreateDate(currentCampaign.create_date)}</p>
              </Col>
            </Row>
          )}

          <Row>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_name">{Strings('name')}</Label>
              <Input
                type="text"
                name="campaign_name"
                id="campaign_name"
                defaultValue={currentCampaign && currentCampaign.coln}
                innerRef={register}
              />
              {errors.campaign_name && (
                <p className="error-validation text-left">
                  {errors.campaign_name.message}
                </p>
              )}
            </FormGroup>
            <FormGroup tag={Col} md={3}>
              <Label for="campaign_event_type">{Strings('event_type')} *</Label>
              <Input
                type="select"
                name="campaign_event_type"
                id="campaign_event_type"
                innerRef={register}
                defaultValue={currentCampaign && currentCampaign.colevt && currentCampaign.colevt}
                onChange={(evt) => setCategory(evt.target.value)}
              >
                <option value=''>{Strings('select')}</option>
                <option value={2}>Popular</option>
                <option value={4}>Business</option>
                <option value={3}>Modern</option>
                <option value={0}>Music</option>
                <option value={1}>Sports</option>
              </Input>
              {errors.campaign_event_type && (
                <p className="error-validation text-left">
                  {errors.campaign_event_type.message}
                </p>
              )}
            </FormGroup>
            <FormGroup tag={Col} md={3}>
              <Label for="campaign_event_style">{Strings('change_event_style')}</Label>
              <Input
                type="select"
                name="campaign_event_style"
                id="campaign_event_style"
                innerRef={register}
              >
                <option value=''>{Strings('select')}</option>
                {styles && styles.length > 0 && styles.map((item) => (
                  <option
                    key={item.id}
                    value={item.id}
                  >
                    {item.la}
                  </option>
                ))}
              </Input>
              {errors.campaign_event_type && (
                <p className="error-validation text-left">
                  {errors.campaign_event_type.message}
                </p>
              )}
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={12}>
              <Label for="campaign_description">{Strings('description')}</Label>
              <Input
                type="textarea"
                name="campaign_description"
                id="campaign_description"
                defaultValue={currentCampaign && currentCampaign.cold}
                innerRef={register}
              />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_local">{Strings('local')}</Label>
              <Input
                type="text"
                name="campaign_local"
                id="campaign_local"
                defaultValue={currentCampaign && currentCampaign.colloc}
                innerRef={register}
              />
            </FormGroup>
            <FormGroup tag={Col} md={6}>
              <Label>{Strings('address')}</Label>
              <Autocomplete
                className='form-control'
                defaultValue={currentCampaign && currentCampaign.colad && currentCampaign.colad}
                apiKey={GOOGLE_MAPS_API_KEY}
                onPlaceSelected={toggleGoogleAddress}
                options={{ types: [] }}
              />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_pricing">{Strings('pricing')}</Label>
              <Input
                type="text"
                name="campaign_pricing"
                id="campaign_pricing"
                innerRef={register}
                defaultValue={currentCampaign && currentCampaign.colpri}
              />
            </FormGroup>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_tickets">{Strings('how_to_buy_a_ticket')}</Label>
              <Input
                type="text"
                name="campaign_tickets"
                id="campaign_tickets"
                innerRef={register}
                defaultValue={currentCampaign && currentCampaign.coltic}
              />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={12}>
              <Label for="campaign_participants">{Strings('participants')}</Label>
              <Input
                type="text"
                name="campaign_participants"
                id="campaign_participants"
                innerRef={register}
                defaultValue={currentCampaign && currentCampaign.colpar}
              />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_info_url">{Strings('info_url')}</Label>
              <Input
                type="text"
                name="campaign_info_url"
                id="campaign_info_url"
                innerRef={register}
                defaultValue={currentCampaign && currentCampaign.colurl}
              />
            </FormGroup>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_custom_url">{Strings('custom_url')}</Label>
              <Input
                type="text"
                name="campaign_custom_url"
                id="campaign_custom_url"
                innerRef={register}
                defaultValue={currentCampaign && currentCampaign.colcurl}
              />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_date_start">{Strings('date_start')}</Label>
              <DatePicker
                className="form-control"
                selected={dateStart}
                onChange={(date) => setDateStart(date)}
                minDate={new Date()}
              />
            </FormGroup>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_date_end">{Strings('date_end')}</Label>
              <DatePicker
                className="form-control"
                selected={dateEnd}
                onChange={(date) => setDateEnd(date)}
                minDate={new Date()}
              />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={12}>
              <Label for="campaign_organization">{Strings('organization')}</Label>
              <Input
                type="text"
                name="campaign_organization"
                id="campaign_organization"
                innerRef={register}
                defaultValue={currentCampaign && currentCampaign.colorg}
              />
            </FormGroup>
          </Row>
          <Button
            color="primary"
            className="mt-1"
            onClick={handleSubmit(onSubmit)}
          >
            {Strings('save')}
          </Button>
        </CardBody>
      </Card>

      <Card className='mt-5'>
        <CardBody>
          <p>
            {Strings('other_options')}
          </p>
          <Button
            color="primary"
            className="mt-1 mr-2"
            onClick={toggleModalCreateVideo}
          >
            {Strings('create_video')}
          </Button>
          <Button
            color="primary"
            className="mt-1 mr-2"
            onClick={() => {
              toggleModalAddRemix();
              getUserVideos();
            }}
          >
            {Strings('add_video_to_campaign')}
          </Button>
          <Button
            color="danger"
            className="mt-1"
            onClick={deleteCampaign}
          >
            {Strings('delete_campaign')}
          </Button>
        </CardBody>
      </Card>

      <Modal
        isOpen={modalConfirmDelete}
        toggle={toggleModalConfirmDelete}
      >
        <ModalHeader toggle={toggleModalConfirmDelete}>
          {Strings('confirm_delete')}
        </ModalHeader>
        <ModalBody>
          <p>
            {Strings('are_you_sure_that_you_want_to_delete')}
          </p>
          <div className="text-left">
            <Button
              className="btn-wide mb-2 mr-2"
              size="md"
              color="danger"
              onClick={confirmDeleteCampaign}
            >
              {Strings('yes_delete_campaign')}
            </Button>
            <Button
              className="btn-wide mb-2 mr-2"
              size="md"
              color="primary"
              onClick={toggleModalConfirmDelete}
            >
              {Strings('cancel')}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={modalAddRemix}
        toggle={toggleModalAddRemix}
      >
        <ModalHeader toggle={toggleModalAddRemix}>
          {Strings('add_video_to_campaign')}
        </ModalHeader>
        <ModalBody>
          <div className="btn-actions-pane-right mb-3">
            <ButtonGroup size="sm">
              <Button
                className={"btn-shadow " + classnames({ active: activeTab === '1' })}
                onClick={() => { setActiveTab('1') }}
              >
                {Strings('your_videos')}
              </Button>
              <Button
                className={"btn-shadow " + classnames({ active: activeTab === '2' })}
                onClick={() => { setActiveTab('2'); }}
              >
                {Strings('manual')}
              </Button>
            </ButtonGroup>
          </div>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <ListGroup className='add-videos-list-container'>
                {userVideos && userVideos.length > 0 && userVideos.map((value) => (
                  <ListGroupItem key={value.rid}>
                    <div className='widget-content p-0'>
                      <div className='widget-content-wrapper'>
                        <div className='widget-content-left mr-3'>
                          <a
                            href={`${PUBLIC_VIDEOS_URL}+${value.tid}`}
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            <img
                              width={92}
                              className='rounded'
                              src={COVER_IMG_LOCATION.replace('{id}', value.rid)}
                              alt={value.rti}
                            />
                          </a>
                        </div>
                        <div className='widget-content-left'>
                          <div className='widget-heading'>
                            {value.rti}
                          </div>
                          <div className='widget-subheading'>
                            {`*${value.rstag}`}
                          </div>
                        </div>
                        <div className='widget-content-right'>
                          <Button
                            color='secondary'
                            onClick={() => onSubmitAdd(value)}
                          >
                            {Strings('add')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </ListGroupItem>
                ))}
              </ListGroup>
            </TabPane>
            <TabPane tabId="2">
              <Row>
                <FormGroup tag={Col} md={12}>
                  <Label for="rid">{Strings('rid')}</Label>
                  <Input
                    type="text"
                    name="rid"
                    id="rid"
                    innerRef={registerAdd}
                  />
                </FormGroup>
              </Row>
              <div className="text-left">
                <Button
                  className="btn-wide mb-2 mr-2"
                  size="md"
                  color="primary"
                  onClick={handleSubmitAdd(onSubmitAdd)}
                >
                  {Strings('add')}
                </Button>
              </div>
            </TabPane>
          </TabContent>


        </ModalBody>
      </Modal>

      <Modal
        isOpen={modalCreateVideo}
        toggle={toggleModalCreateVideo}
      >
        <ModalHeader toggle={toggleModalCreateVideo}>
          {Strings('create_video')}
        </ModalHeader>
        <ModalBody className='text-center'>
          <Button
            size='lg'
            className='mr-2 mb-2'
            outline
            color='primary'
            onClick={() => {
              history.push(`/campaign/create-video?create=2`);
            }}
          >
            {Strings('auto_video')}
          </Button>
          <Button
            size='lg'
            className='mr-2 mb-2'
            outline
            color='primary'
            onClick={() => {
              history.push(`/campaign/create-video`);
            }}
          >
            {Strings('step_by_step')}
          </Button>
        </ModalBody>
      </Modal>

    </Fragment>
  );
}
